import {
  addPatientNote as sdkAddPatientNote,
  deletePatientNote as sdkDeletePatientNote,
  editPatientNote as sdkEditPatientNote,
  listPatientNotes as sdkListPatientNotes,
  type NoteResponseDto,
} from "~/lib/api";

/**
 * Network + mapping layer for patient notes (V2 live management). Each wrapper
 * maps HTTP status codes to a discriminated-union result so the caller never
 * has to inspect raw status codes. The SDK functions are aliased to avoid name
 * collisions with the wrappers exported here.
 */

const GENERIC_NOTE_ERROR = "Something went wrong. Please try again.";

export type FetchNotesResult =
  | { status: "ok"; notes: NoteResponseDto[] }
  | { status: "not_found" }
  | { status: "error"; message: string };

export type NoteResult =
  | { status: "ok"; note: NoteResponseDto }
  | { status: "forbidden" }
  | { status: "not_found" }
  | { status: "error"; message: string };

export type DeleteNoteResult =
  | { status: "ok" }
  | { status: "forbidden" }
  | { status: "not_found" }
  | { status: "error"; message: string };

/** Fetch all notes for a patient, oldest first. */
export async function fetchNotes(sessionId: string, patientId: string): Promise<FetchNotesResult> {
  const result = await sdkListPatientNotes({ path: { sessionId, patientId } });
  if (result.data && !result.error) return { status: "ok", notes: result.data };
  if (result.response.status === 404) return { status: "not_found" };
  return { status: "error", message: `Couldn't load notes (${result.response.status}).` };
}

/** Add a new note to a patient. */
export async function addNote(
  sessionId: string,
  patientId: string,
  body: string,
): Promise<NoteResult> {
  const result = await sdkAddPatientNote({
    path: { sessionId, patientId },
    body: { body },
  });
  if (result.data && !result.error) return { status: "ok", note: result.data };
  if (result.response.status === 404) return { status: "not_found" };
  return { status: "error", message: GENERIC_NOTE_ERROR };
}

/** Edit the caller's own note. 403 → forbidden (only the author may edit). */
export async function editNote(
  sessionId: string,
  patientId: string,
  noteId: string,
  body: string,
): Promise<NoteResult> {
  const result = await sdkEditPatientNote({
    path: { sessionId, patientId, noteId },
    body: { body },
  });
  if (result.data && !result.error) return { status: "ok", note: result.data };
  switch (result.response.status) {
    case 403:
      return { status: "forbidden" };
    case 404:
      return { status: "not_found" };
    default:
      return { status: "error", message: GENERIC_NOTE_ERROR };
  }
}

/** Delete the caller's own note. 403 → forbidden (only the author may delete). */
export async function deleteNote(
  sessionId: string,
  patientId: string,
  noteId: string,
): Promise<DeleteNoteResult> {
  const result = await sdkDeletePatientNote({ path: { sessionId, patientId, noteId } });
  // deletePatientNote returns 200 with no body on success (sdk.gen.ts uses delete<unknown,...>)
  if (!result.error) return { status: "ok" };
  switch (result.response.status) {
    case 403:
      return { status: "forbidden" };
    case 404:
      return { status: "not_found" };
    default:
      return { status: "error", message: GENERIC_NOTE_ERROR };
  }
}
